/**
 * useCardDrawerLayout — chrome/layout state for the kanban CardDetailDrawer:
 * narrow-viewport tracking, pin state, operator-resizable width (overlay +
 * inline), the published CSS variables, universal-dock occlusion, the modal
 * Esc handler, and the wrapper/aside class + style strings for the three
 * render modes (inline / pinned / overlay).
 *
 * Chrome class strings live in resolveCardDrawerChrome.ts. Wrapper style
 * (hide vs reserve width) lives in resolveInlineDrawerWrapperStyle.ts.
 */
import { useCallback, useEffect, useState } from 'react'
import { acquireOverlay } from '../../../components/overlayChromeStore'
import type { Card } from '../../types'
import {
  DEFAULT_WIDTH_OVERLAY_PX,
  DEFAULT_WIDTH_PINNED_PX,
  MAX_DRAWER_WIDTH_PX,
  MIN_DRAWER_WIDTH_PX,
  WIDTH_LS_KEY,
  readSavedDrawerWidth,
} from './drawerChrome'
import {
  OCCLUSION_CSS_VAR,
  PINNED_CSS_VAR,
  useCardDetailPinned,
} from './usePinnedDrawer'
import type { KanbanOpenMode } from './openModePref'
import { resolveCardDrawerChrome } from './resolveCardDrawerChrome'
import { resolveInlineDrawerWrapperStyle } from './resolveInlineDrawerWrapperStyle'

export interface UseCardDrawerLayoutArgs {
  open: boolean
  card: Card | null
  inlineMode: boolean
  inlineActive: boolean
  widthOverride?: number
  onInlineResize?: (deltaPx: number) => void
  onInlineResizeEnd?: () => void
  closeWithFlush: () => Promise<void> | void
  /** ARC-1 MR5 (U14) — per-user card open mode. 'popup' renders the SAME
   *  drawer body in a Trello-style full-screen modal chrome (ignored on
   *  phone viewports where the bottom sheet is already the best chrome). */
  openMode?: KanbanOpenMode
}

export function useCardDrawerLayout({
  open,
  card,
  inlineMode,
  inlineActive,
  widthOverride,
  onInlineResize,
  onInlineResizeEnd,
  closeWithFlush,
  openMode = 'drawer',
}: UseCardDrawerLayoutArgs) {
  // Phone viewport tracker — subscribes to matchMedia so the inline-mode
  // wrapper can flip width semantics on rotate / window-resize. SSR-safe.
  const [isNarrowViewport, setIsNarrowViewport] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    try {
      return window.matchMedia('(max-width: 767.98px)').matches
    } catch {
      return false
    }
  })
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    let mql: MediaQueryList
    try {
      mql = window.matchMedia('(max-width: 767.98px)')
    } catch {
      return
    }
    const onChange = () => setIsNarrowViewport(mql.matches)
    onChange()
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    }
    // Safari <14 fallback — addListener is deprecated but still works.
    if (typeof mql.addListener === 'function') {
      mql.addListener(onChange)
      return () => mql.removeListener(onChange)
    }
  }, [])

  // ARC-1 MR5 (U14) — Trello-style popup chrome. Applies on md+ only:
  // on phones the drawer already renders as a full-width bottom sheet,
  // which IS the best full-screen treatment for that viewport.
  const popup = openMode === 'popup' && !isNarrowViewport

  // Pin-as-split-pane state. In inline mode the parent right-rail container
  // owns the docked chrome, so we force `pinned=true` and hide the toggle.
  // The hook is still called unconditionally to keep hook order stable.
  // Popup mode overrides both: a full-screen modal is never pinned.
  const [pinnedPref, setPinned] = useCardDetailPinned()
  // Phone + inline is a viewport overlay (Job Hub overflow-hidden clips an
  // absolute bottom sheet). Treat it as unpinned so Esc / backdrop close.
  const pinned = popup || (inlineMode && isNarrowViewport) ? false : inlineMode ? true : pinnedPref

  // Drawer width (operator-resizable from the LEFT edge). Persisted per-device.
  const [drawerWidthLocal, setDrawerWidth] = useState<number>(() =>
    readSavedDrawerWidth() ?? (pinned ? DEFAULT_WIDTH_PINNED_PX : DEFAULT_WIDTH_OVERLAY_PX),
  )
  // In inline mode the parent rail container owns the width via `widthOverride`.
  const drawerWidth = inlineMode && widthOverride
    ? widthOverride
    : drawerWidthLocal
  useEffect(() => {
    if (typeof window === 'undefined') return
    // Don't persist the parent-controlled width — the email rail owns its own.
    if (inlineMode) return
    try {
      window.localStorage.setItem(WIDTH_LS_KEY, String(drawerWidthLocal))
    } catch { /* private mode etc. */ }
  }, [drawerWidthLocal, inlineMode])

  // Drag-resize handler. Mounted on the LEFT edge; pointer events so trackpad
  // + touch both work without separate handler branches.
  const onResizeStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startW = drawerWidth
    const move = (ev: PointerEvent) => {
      const dx = startX - ev.clientX
      const next = Math.min(
        MAX_DRAWER_WIDTH_PX,
        Math.max(MIN_DRAWER_WIDTH_PX, startW + dx),
      )
      setDrawerWidth(next)
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    // Lock cursor + suppress text selection during the drag.
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
  }, [drawerWidth])

  // FEAT-2026-06-07-KANBAN-DRAWER-RESIZE — inline-mode drag handler. Emits the
  // INCREMENTAL pointer delta on every move; the parent clamps + persists.
  const onInlineResizeStart = useCallback((e: React.PointerEvent) => {
    if (!onInlineResize) return
    e.preventDefault()
    e.stopPropagation()
    let lastX = e.clientX
    const move = (ev: PointerEvent) => {
      const delta = ev.clientX - lastX
      lastX = ev.clientX
      if (delta !== 0) onInlineResize(delta)
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
      onInlineResizeEnd?.()
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
  }, [onInlineResize, onInlineResizeEnd])

  // Publish two CSS variables on `<html>` so the rest of the app can react to
  // the drawer state without React state plumbing across packages.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    // BUG-2026-06-16-KANBAN-CARD-RAIL-FLICK — a hidden inline pane occupies
    // zero layout, so it must publish the SAME vars as a closed drawer (none).
    const inlineVisible = !inlineMode || inlineActive
    // A popup floats over the canvas — it occludes nothing in layout
    // terms, so it must publish the SAME vars as a closed drawer (none).
    const isOpen =
      open &&
      card !== null &&
      inlineVisible &&
      !popup &&
      !(inlineMode && isNarrowViewport)
    if (isOpen) {
      root.style.setProperty(OCCLUSION_CSS_VAR, `${drawerWidth}px`)
      // PINNED_CSS_VAR (canvas `padding-right` reservation) is ONLY for the
      // legacy pinned split-pane now — an inline flex sibling reserves its own
      // width in normal flow, so publishing it too would double-reserve.
      if (pinned && !inlineMode) {
        root.style.setProperty(PINNED_CSS_VAR, `${drawerWidth}px`)
      } else {
        root.style.removeProperty(PINNED_CSS_VAR)
      }
    } else {
      root.style.removeProperty(PINNED_CSS_VAR)
      root.style.removeProperty(OCCLUSION_CSS_VAR)
    }
    return () => {
      root.style.removeProperty(PINNED_CSS_VAR)
      root.style.removeProperty(OCCLUSION_CSS_VAR)
    }
  }, [open, pinned, inlineMode, inlineActive, card, drawerWidth, popup, isNarrowViewport])

  // Register with the shared overlayChromeStore so UniversalDock hides itself
  // for the lifetime of an open card detail (not scoped to pinned).
  useEffect(() => {
    if (!(open && card)) return
    // Only claim the overlay while the card pane is actually visible.
    if (inlineMode && !inlineActive) return
    const release = acquireOverlay()
    return release
  }, [open, card, inlineMode, inlineActive])

  // Esc closes ONLY when the drawer is acting as a modal overlay. First Esc
  // blurs a focused field; a second Esc (targeting the body) closes.
  useEffect(() => {
    if (!open) return
    if (pinned) return
    // A popup hidden in place by the rail keep-alive must not swallow Esc.
    if (popup && inlineMode && !inlineActive) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const t = e.target
        if (
          t instanceof HTMLElement &&
          (t instanceof HTMLInputElement ||
            t instanceof HTMLTextAreaElement ||
            t instanceof HTMLSelectElement ||
            t.isContentEditable)
        ) {
          e.stopPropagation()
          t.blur()
          return
        }
        void closeWithFlush()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, pinned, closeWithFlush, popup, inlineMode, inlineActive])

  const chrome = resolveCardDrawerChrome({
    popup,
    inlineMode,
    isNarrowViewport,
    pinned,
  })
  // BUG-2026-08-26-KANBAN-CLOSED-RAIL-BLANK — hide AND drop width when
  // the inline pane is closed/collapsed. Pre-fix only hid on !inlineActive,
  // so a closed rail with the Card tab still selected reserved ~320px of
  // empty flex sibling (Table / Calendar / Dashboard blank right band).
  const wrapperStyle = resolveInlineDrawerWrapperStyle({
    popup,
    inlineMode,
    inlineActive,
    open,
    isNarrowViewport,
    drawerWidth,
    pinned,
  })
  const asideStyle = popup
    ? undefined
    : inlineMode
    ? undefined
    : pinned
    ? undefined
    : { width: `min(${drawerWidth}px, 100vw)` }

  return {
    isNarrowViewport,
    pinned,
    setPinned,
    popup,
    drawerWidth,
    setDrawerWidth,
    onResizeStart,
    onInlineResizeStart,
    wrapperClass: chrome.wrapperClass,
    wrapperStyle,
    asideClass: chrome.asideClass,
    asideStyle,
    escapeToViewport: chrome.escapeToViewport,
  }
}
